import { TypedDocumentNode, useQuery } from "@apollo/client";
import { 
  SxProps, 
  Typography, 
  ListSubheader,
  ListSubheaderProps,
  AutocompleteRenderGroupParams
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
} from "react";
import { FieldError } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { ErrorDisplay } from "./ErrorDisplay";

type OmnisearchPaginationModel = {
  search: string;
  page: number;
  pageSize: number;
  sort: GridSortModel;
};

function DefaultListSubheader(props: ListSubheaderProps) {
  return <ListSubheader {...props} />;
}
DefaultListSubheader.muiSkipListHighlight = true;

/**
 * Props for the GroupedAsyncAutocomplete component.
 */
interface GroupedAsyncAutocompleteProps<
  TData,
  TOption extends { id: string | number },
  TGroupKey extends string,
  TAdditionalVars extends Record<string, unknown>,
  TMultiple extends boolean | undefined = false
> {
  /** GraphQL query document */
  query: TypedDocumentNode<TData, TAdditionalVars>;
  /** Function to extract items from the query result */
  getItems: (data: TData) => TOption[];
  /** Function to extract the label from an option */
  getOptionLabel: (option: TOption) => string;
  /** Function to determine which group an option belongs to */
  groupBy: (option: TOption) => TGroupKey;
  /** Optional array to control the order of group display */
  groupOrder?: TGroupKey[];
  /** Custom group renderer */
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  placeholderLabel?: string;
  /** Additional variables for the query */
  variables?: Partial<TAdditionalVars>;
  /** Initial pagination model */
  initialPaginationModel?: Partial<OmnisearchPaginationModel>;
  /** Initial page size for pagination */
  initialPageSize?: number;
  /** Callback for when an option is selected */
  onChange?: (
    value: TMultiple extends true ? TOption[] : TOption | null
  ) => void;
  /** The value of the autocomplete */
  value?: TMultiple extends true ? TOption[] : TOption | null;
  /** Error to display */
  error?: FieldError;
  /** Display in a disabled state */
  disabled?: boolean;
  /** Additional search string to append to the search query */
  additionalSearchString?: string;
  helperText?: string | React.JSX.Element;
  additionalEndAdornment?: React.ReactNode;
  /** Enable multiple selection mode */
  multiple?: TMultiple;
  /** Filter selected options from dropdown */
  filterSelectedOptions?: boolean;
  /** Function to disable specific options */
  getOptionDisabled?: (option: TOption) => boolean;
  /** Custom option renderer */
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TOption
  ) => React.ReactNode;
  sx?: SxProps;
}

/**
 * GroupedAsyncAutocomplete Component
 * A generic autocomplete component that supports pagination, GraphQL integration,
 * grouping, and both single and multiple selection modes.
 *
 * @param query - The GraphQL query document to fetch data.
 * @param getItems - Function to extract items from the query result.
 * @param getOptionLabel - Function to extract the label from an option.
 * @param groupBy - Function to determine which group an option belongs to.
 * @param groupOrder - Optional array to control group display order.
 * @param renderGroup - Custom group renderer.
 * @param variables - Variables for the query.
 * @param onChange - Callback for when an option is selected.
 * @param value - The value of the autocomplete (single item or array for multiple mode).
 * @param error - Error to display.
 * @param multiple - Enable multiple selection mode.
 * @param filterSelectedOptions - Filter selected options from dropdown (multiple mode only).
 * @param getOptionDisabled - Function to disable specific options.
 * @param renderOption - Custom option renderer.
 * @returns A JSX element representing the grouped paginated autocomplete component.
 *
 * @example Single Selection with Grouping
 * ```tsx
 * <GroupedAsyncAutocomplete
 *   query={GROUPED_FIRE_DEPARTMENTS_QUERY}
 *   getItems={(data) => [...data.primary, ...data.supplemental]}
 *   getOptionLabel={(dept) => `${dept.name} ${dept.city}, ${dept.state}`}
 *   groupBy={(dept) => dept.isPrimary ? 'primary' : 'supplemental'}
 *   groupOrder={['primary', 'supplemental']}
 *   onChange={onChange}
 *   value={value}
 *   error={error}
 * />
 * ```
 */
function GroupedAsyncAutocompleteComponent<
  TData,
  TOption extends { id: string | number },
  TGroupKey extends string,
  TAdditionalVars extends Record<string, unknown>,
  TMultiple extends boolean | undefined = false
>(
  {
    query,
    getItems,
    getOptionLabel,
    groupBy,
    groupOrder,
    renderGroup,
    placeholderLabel = "Search",
    variables = {},
    initialPageSize = 20,
    onChange,
    value,
    error,
    disabled,
    additionalSearchString = "",
    helperText,
    additionalEndAdornment,
    multiple = false,
    filterSelectedOptions = false,
    getOptionDisabled,
    renderOption,
    sx,
  }: GroupedAsyncAutocompleteProps<TData, TOption, TGroupKey, TAdditionalVars, TMultiple>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  type VariablesType = OmnisearchPaginationModel & Partial<TAdditionalVars>;
  const {
    omnisearch,
    setOmnisearch,
    paginationModel: { page, pageSize },
    setPaginationModel,
    sortModel,
  } = useOmnisearchDatagrid({
    persistenceKey: `grouped-async-autocomplete-${
      (query.definitions[0] as any).name.value
    }`,
    isURLDriven: false,
    initialPageSize,
    initialSortModel: [{ field: "name", sort: "asc" }],
  });
  const [options, setOptions] = useState<TOption[]>([]);
  const [hasMoreOptions, setHasMoreOptions] = useState<boolean>(true);

  useEffect(() => {
    setOptions([]);
    setHasMoreOptions(true);
  }, [additionalSearchString]);

  const search = useMemo(
    () => `${additionalSearchString} ${omnisearch}`.trim(),
    [additionalSearchString, omnisearch]
  );
  const [debouncedSearch] = useDebounce(search, 150);

  const variablesWithPagination: VariablesType = useMemo(
    () => ({
      ...variables,
      search,
      page,
      pageSize,
      sort: sortModel,
    }),
    [page, pageSize, search, sortModel, variables]
  );

  const {
    fetchMore,
    loading,
    error: dataLoadingError,
  } = useQuery<TData, VariablesType>(query, {
    variables: variablesWithPagination,
    notifyOnNetworkStatusChange: true,
    onCompleted: (newData) => {
      const items = newData ? getItems(newData) : [];
      setOptions((prevOptions) => [...prevOptions, ...items]);
      setHasMoreOptions(items.length >= pageSize);
    },
    skip: disabled,
  });

  // Handler for input changes in the autocomplete
  const handleInputChange = useCallback(
    (event: SyntheticEvent<Element, Event>, newInputValue: string) => {
      setOmnisearch(newInputValue);
      setPaginationModel({
        page: 0, // Reset the page when we get a new input
        pageSize,
      });
      setOptions([]); // Clear the previous options
    },
    [pageSize, setOmnisearch, setPaginationModel, setOptions]
  );

  // Handler for scrolling in the autocomplete listbox
  const handleScroll = useCallback(
    async (event: React.UIEvent<HTMLUListElement>) => {
      // Add a buffer to trigger loading before reaching the absolute bottom
      const BUFFER_PX = 24;
      const bottom =
        event.currentTarget.scrollHeight <=
        event.currentTarget.scrollTop +
          event.currentTarget.clientHeight +
          BUFFER_PX;

      if (bottom && hasMoreOptions && !loading) {
        await fetchMore({
          variables: {
            ...variablesWithPagination,
            search: `${debouncedSearch} ${additionalSearchString}`.trim(),
            page: page + 1,
            pageSize,
          },
        });
        setPaginationModel({
          page: page + 1,
          pageSize,
        });
      }
    },
    [
      additionalSearchString,
      debouncedSearch,
      fetchMore,
      hasMoreOptions,
      loading,
      page,
      pageSize,
      setPaginationModel,
      variablesWithPagination,
    ]
  );

  // Sort options by group order if provided
  const sortedOptions = useMemo(() => {
    if (!groupOrder) {
      return options;
    }

    return [...options].sort((a, b) => {
      const groupA = groupBy(a);
      const groupB = groupBy(b);
      const indexA = groupOrder.indexOf(groupA);
      const indexB = groupOrder.indexOf(groupB);
      
      // If group not found in order array, put it at the end
      const priorityA = indexA === -1 ? groupOrder.length : indexA;
      const priorityB = indexB === -1 ? groupOrder.length : indexB;
      
      return priorityA - priorityB;
    });
  }, [options, groupBy, groupOrder]);

  const defaultRenderGroup = useCallback((params: AutocompleteRenderGroupParams) => (
    <li key={params.key}>
      <DefaultListSubheader>{params.group}</DefaultListSubheader>
      <ul style={{ padding: 0 }}>{params.children}</ul>
    </li>
  ), []);

  return (
    <>
      {dataLoadingError && (
        <Typography variant="body2" color="error">
          An error occurred while searching.
        </Typography>
      )}
      <Autocomplete
        ref={ref}
        multiple={multiple}
        filterSelectedOptions={filterSelectedOptions}
        options={sortedOptions}
        getOptionLabel={getOptionLabel}
        groupBy={groupBy}
        renderGroup={renderGroup || defaultRenderGroup}
        // Disable the built-in filtering since it is handled server-side
        filterOptions={(x) => x}
        onInputChange={handleInputChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onChange={(_, value) => {
          onChange?.(
            value as TMultiple extends true ? TOption[] : TOption | null
          );
        }}
        value={value}
        disabled={disabled}
        getOptionDisabled={getOptionDisabled}
        renderOption={renderOption}
        sx={sx}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            label={placeholderLabel}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {additionalEndAdornment}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: handleScroll,
        }}
        loading={loading}
        loadingText="Loading..."
        noOptionsText="No options found."
      />
      <ErrorDisplay error={error} helperText={helperText} />
    </>
  );
}

const GroupedAsyncAutocomplete = forwardRef(GroupedAsyncAutocompleteComponent) as <
  TData,
  TOption extends { id: string | number },
  TGroupKey extends string,
  TAdditionalVars extends Record<string, unknown>,
  TMultiple extends boolean | undefined = false
>(
  props: GroupedAsyncAutocompleteProps<TData, TOption, TGroupKey, TAdditionalVars, TMultiple> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => React.ReactElement;

export default GroupedAsyncAutocomplete;